import React, { useEffect, useState } from 'react';
import { AlertTriangle, Plus, Send } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { InviteActionsDropdown } from '@/components/invites/InviteActionsDropdown';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Time } from '@/components/ui/time';
import { useToast } from '@/contexts/ToastContext';
import type { InviteRow, AvailableCoupon } from '@/api/invitationsApi';
import { isApiClientError } from '@/api/errors';
import {
  useInviteQuota, useInvitesList, useAvailableCoupons,
  useCreateInvite, useResendInvite, useRevokeInvite,
} from '@/hooks/useInvitations';

const statusVariant = (s: InviteRow['status']): 'success' | 'destructive' | 'tertiary' | 'default' => {
  switch (s) {
    case 'redeemed': return 'success';
    case 'revoked': return 'destructive';
    case 'expired': return 'default';
    case 'pending': return 'tertiary';
  }
};

interface SendInviteModalProps {
  open: boolean;
  discountSummary: string;
  coupons: AvailableCoupon[];
  defaultCoupon: string;
  onClose: () => void;
  onSuccess: (recipientEmail: string) => void;
}

const SendInviteModal: React.FC<SendInviteModalProps> = ({
  open, discountSummary, coupons, defaultCoupon, onClose, onSuccess,
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [selectedCoupon, setSelectedCoupon] = useState(defaultCoupon);
  const [error, setError] = useState<string | null>(null);
  const createInvite = useCreateInvite();

  const selectedSummary =
    coupons.find((t) => t.name === selectedCoupon)?.summary ?? discountSummary;

  useEffect(() => {
    if (open) setSelectedCoupon(defaultCoupon);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setRecipientEmail('');
    setSelectedCoupon(defaultCoupon);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createInvite.mutateAsync({
        recipient_email: recipientEmail,
        coupon: selectedCoupon || undefined,
      });
      onSuccess(recipientEmail);
      reset();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to send invite');
    }
  };

  const submitting = createInvite.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send invite</DialogTitle>
          <DialogDescription>
            The recipient gets {selectedSummary} on their first paid subscription.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="invite-email">Email</Label>
              <Input
                id="invite-email"
                type="email"
                required
                autoFocus
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="friend@example.com"
              />
            </div>

            {coupons.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="invite-coupon">Reward</Label>
                <Select value={selectedCoupon} onValueChange={setSelectedCoupon}>
                  <SelectTrigger id="invite-coupon">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {coupons.map((t) => (
                      <SelectItem key={t.name} value={t.name}>{t.summary}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="tertiary" size="dense" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="dense" disabled={submitting}>
              {submitting ? 'Sending...' : 'Send invite'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const Invites: React.FC = () => {
  const toast = useToast();
  const [modalOpen, setModalOpen] = useState(false);

  const { data: quota, error: quotaError } = useInviteQuota();
  const { data: invitesPage, isLoading, error: listError } = useInvitesList({ page: 1, perPage: 50 });
  const { data: couponsData, error: couponsError } = useAvailableCoupons();
  const resend = useResendInvite();
  const revoke = useRevokeInvite();

  const invites = invitesPage?.items ?? [];
  const coupons = couponsData?.coupons ?? [];
  const defaultCoupon = couponsData?.default ?? '';

  // A 403 means the role lacks create:invitations — the page renders a single
  // access-denied state for that, so don't also toast about the coupons call.
  // The error is only an ApiClientError for HTTP responses; a network/TLS
  // failure throws a plain TypeError, so guard before calling isForbidden().
  const accessDenied =
    (isApiClientError(quotaError) && quotaError.isForbidden()) ||
    (isApiClientError(listError) && listError.isForbidden());

  // Surface a load failure for the reward options once (react-query won't retry
  // it — see useAvailableCoupons retry:false).
  useEffect(() => {
    if (couponsError && !(isApiClientError(couponsError) && couponsError.isForbidden())) {
      toast.error('Failed to load invite reward options.', 'Coupons unavailable');
    }
  }, [couponsError, toast]);

  const handleSendSuccess = (recipientEmail: string) => {
    setModalOpen(false);
    toast.success(`Invite sent to ${recipientEmail}.`, 'Invite sent');
    // The list/quota refresh is handled by the mutation's cache invalidation.
  };

  const handleResend = async (id: string) => {
    try {
      await resend.mutateAsync(id);
      toast.success('Invite resent.', 'Sent');
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Failed to resend invite.', 'Resend failed');
    }
  };

  const handleRevoke = async (id: string) => {
    try {
      await revoke.mutateAsync(id);
      toast.success('Invitation revoked.', 'Revoked');
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Failed to revoke invite.', 'Revoke failed');
    }
  };

  // Treat `quota === undefined` as "not loaded yet" — distinct from
  // `count_remaining === 0`. Without this distinction the page flashes the
  // empty-state alert + disabled button on every refresh until data lands.
  const isQuotaLoaded = quota != null;
  const remaining = quota?.count_remaining ?? 0;
  const discountSummary = quota?.summary ?? 'a discount';
  const isOutOfInvites = isQuotaLoaded && remaining === 0;

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto pt-5 pb-6">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Invites</h1>
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <Badge variant="tertiary" className="mr-2">
                {isQuotaLoaded ? `${remaining} remaining` : '— remaining'}
              </Badge>
              <span>
                Send invites to give friends {discountSummary} on their first paid subscription.
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            size="default"
            onClick={() => setModalOpen(true)}
            disabled={!isQuotaLoaded || isOutOfInvites}
          >
            <Plus className="h-4 w-4 stroke-[1.5]" />
            Send invite
          </Button>
        </div>

        {accessDenied ? (
          <EmptyState
            icon={AlertTriangle}
            title="No access to invites"
            description="Your role doesn't have permission to view or send invites. Ask an admin if you think this is a mistake."
          />
        ) : listError ? (
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load invites"
            description={listError.message}
          />
        ) : !isLoading && invites.length === 0 ? (
          <EmptyState
            icon={Send}
            title="No invites yet"
            description={
              remaining > 0
                ? 'Click "Send invite" to send your first one. The recipient gets a discount on signup.'
                : 'When you send an invite, it will appear here.'
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
                <TableHead className="w-[160px]">Sent</TableHead>
                <TableHead className="w-[160px]">Coupon</TableHead>
                <TableHead>Redeemed by</TableHead>
                <TableHead className="w-[60px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell />
                  </TableRow>
                ))
              ) : (
                invites.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {inv.recipient_email}
                      </div>
                    </TableCell>
                    <TableCell><Badge variant={statusVariant(inv.status)}>{inv.status}</Badge></TableCell>
                    <TableCell><Time value={inv.created_at} /></TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {inv.coupon}
                    </TableCell>
                    <TableCell>{inv.redeemed_by_email ?? '—'}</TableCell>
                    <TableCell className="text-right">
                      <InviteActionsDropdown
                        onResend={inv.status === 'pending' ? () => void handleResend(inv.id) : undefined}
                        onRevoke={inv.status === 'pending' ? () => void handleRevoke(inv.id) : undefined}
                        isResending={resend.isPending && resend.variables === inv.id}
                        isRevoking={revoke.isPending && revoke.variables === inv.id}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}

        <SendInviteModal
          open={modalOpen}
          discountSummary={discountSummary}
          coupons={coupons}
          defaultCoupon={defaultCoupon}
          onClose={() => setModalOpen(false)}
          onSuccess={(email) => handleSendSuccess(email)}
        />
      </div>
    </div>
  );
};

export default Invites;
